import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Header,
  Segment,
  Table,
  Loader,
  Message,
  Button,
  Icon,
  Dropdown,
  Label,
  Statistic,
  Grid,
  Input,
  Modal,
  TextArea,
  Form,
  Popup
} from 'semantic-ui-react';

import {
  fetchPipeline,
  reconcileClone,
  retryClone,
  retryNormalize,
  smokeTestClone,
  seedClone,
  deleteClone,
  setFilter,
  clearFilter,
  clearLastSmokeTest,
  clearDeferredSeed,
  selectCloneRows,
  selectCloneStatus,
  selectCloneError,
  selectCloneFilter,
  selectCloneStatusCounts,
  selectActionStatus,
  selectLastSmokeTest,
  selectDeferredSeed,
  selectMockMode
} from '../../../slices/voiceCloningSlice';
import {
  CLONE_STATUSES,
  CLONE_STATUS_MAP,
  IN_FLIGHT_STATUSES,
  SUMMARY_STATUS_KEYS
} from '../../../components/voiceCloning/cloneStatusConfig';
import {
  IN_FLIGHT_NORMALIZE_STATUSES,
  deriveNormalizeStatusKey,
  getNormalizeStatus
} from '../../../components/voiceCloning/normalizeStatusConfig';
import CloneStatusBadge from '../../../components/voiceCloning/CloneStatusBadge';
import CloneStatusLegend from '../../../components/voiceCloning/CloneStatusLegend';
import NormalizeStatusBadge from '../../../components/voiceCloning/NormalizeStatusBadge';

// Phase 4a §8.2 — full voice-readiness strip anchored above the table when
// the user has filtered to a single book. The invalidation action lets us
// force a refetch the moment a clone flips to `success` (§9.3).
import BookVoiceStatsStrip from '../../../components/voiceStats/BookVoiceStatsStrip';
import { invalidateBookVoiceStats } from '../../../slices/voiceStatsSlice';
// Phase 4a follow-up — the pipeline page is also a valid entry point for a
// Book Creator to land on a specific book (e.g. via deep link from the clone
// status table on BookCreatorLandingPage). If the BC skipped the worksheet,
// processedVersionItems hasn't been populated and ScriptVersionSelector —
// embedded via BookVoiceStatsStrip variant="full" — renders its disabled
// empty state. Dispatch fetchFullBookDataSet on filter.bookId change, gated
// on the storyEditor slice being idle so we don't double-fetch.
import {
  fetchFullBookDataSet,
  clearStory
} from '../../../slices/storyEditorSlice';

// The standard "© AuthoratorAI / build info" footer used across every other
// authenticated landing page. It was missing here, which made the page feel
// inconsistent with neighbours like AddBooksLandingPage / BookCreatorLandingPage.
import { AuthStdFooter } from '../../../utils/auth-std-footer';

/**
 * VoiceCloningPipeline
 *
 * Book Creator page showing every approved speech-sample that has been
 * (or can be) seeded into the ElevenLabs voice-cloning pipeline, grouped by
 * literary group → book → character → variation.
 *
 * Responsibilities:
 *   - Load all rows for the current user on mount.
 *   - For approved samples with no clone yet, render a "Seed" action so the
 *     BC can launch the pipeline manually (new approvals auto-seed).
 *   - Poll the pipeline on a gentle interval while any row is in flight,
 *     so status transitions surface without manual refresh.
 *   - Let the BC filter by state / book / free-text search.
 *   - Expose per-row actions: seed, reconcile, retry, smoke-test, delete.
 */

const POLL_INTERVAL_MS = 5000;

// ---------- tiny pure helpers -----------------------------------------------

const sortRows = (rows) =>
  [...rows].sort((a, b) => {
    const lg = (a.litGroupName || '').localeCompare(b.litGroupName || '');
    if (lg !== 0) return lg;
    const bk = (a.bookTitle || '').localeCompare(b.bookTitle || '');
    if (bk !== 0) return bk;
    const ch = (a.characterName || '').localeCompare(b.characterName || '');
    if (ch !== 0) return ch;
    return (a.variationName || '').localeCompare(b.variationName || '');
  });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
};

// ---------- TruncatedTextCell ------------------------------------------------
//
// Several text columns (Literary Group, Book, Variation) plus the inline
// errorMessage block routinely contain strings long enough to blow out the
// table's horizontal width and force a body-level horizontal scrollbar. We
// can't shrink the source text, but we *can* clip what's visible to a fixed
// box and serve the full string in a hover popup so nothing is lost.
//
// Implementation notes:
//   - `whiteSpace: nowrap` + `textOverflow: ellipsis` is the lowest-friction
//     CSS-only truncation. We pair it with `overflow: hidden` and a hard
//     `maxWidth` so the cell can never push the column wider than expected.
//   - Semantic UI's <Popup> needs a *DOM-element* trigger (not a styled
//     fragment) for hover detection, hence the wrapping <span>.
//   - Empty/falsy values render the em-dash placeholder *without* a popup
//     since there's nothing useful to show.
//   - `mouseEnterDelay` keeps the popup from flashing when the user is just
//     scanning the table.
const TruncatedTextCell = ({
  text,
  maxWidth = 160,
  color,
  fontSize,
  marginTop,
  placeholder = '—'
}) => {
  if (text === null || text === undefined || text === '') {
    return <span style={{ color: color || undefined }}>{placeholder}</span>;
  }
  const baseStyle = {
    display: 'inline-block',
    maxWidth: `${maxWidth}px`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    color: color || undefined,
    fontSize: fontSize || undefined,
    marginTop: marginTop || undefined
  };
  return (
    <Popup
      content={String(text)}
      mouseEnterDelay={250}
      position="top left"
      wide
      trigger={<span style={baseStyle}>{String(text)}</span>}
    />
  );
};

// ---------- per-row component ------------------------------------------------

const CloneRow = ({
  row,
  onAction,
  actionStatus,
  normalizeActionStatus,
  onOpenSmokeTest
}) => {
  const isBusy = !!actionStatus;
  const terminal = CLONE_STATUS_MAP[row.cloneStatus]?.terminal;
  const notSeeded = row.cloneStatus === 'none' || !row.hasClone;

  // Normalize-column derivations. We keep these out of the JSX so the intent
  // is obvious at a glance: the retry button shows only when the status is
  // in a retryable bucket *and* we actually have a VoiceSnippet uuid to hit.
  const normalizeKey = deriveNormalizeStatusKey(row.normalize);
  const normalizeDef = getNormalizeStatus(normalizeKey);
  const vsUuid = row.normalize?.voiceSnippetUuid || null;
  const normalizeBusy = !!normalizeActionStatus;
  const canRetryNormalize = normalizeDef.retryable && !!vsUuid;

  // Phase 3.5 Seed-button gating. `seedCloneForSample` on the server defers
  // any row whose normalize status isn't `ready`, so enabling the Seed button
  // in any other state just produces a silent no-op. Require the sample to be
  // normalized first; when it isn't, we re-label and disable so the BC's next
  // action is unambiguously the Retry icon in the Normalize column.
  const normalizeReady = row.normalize?.status === 'ready';
  const seedAllowed = notSeeded && normalizeReady;
  const seedTooltip = !normalizeReady
    ? 'Normalize this sample first (click the ↻ button in the Normalize column).'
    : 'Seed: export this sample to ElevenLabs and start the clone pipeline.';

  // `lastFailedAttempt` is the server's forensic breadcrumb when a prior
  // clone-side failure has been superseded by a successful re-normalize.
  // It's only populated on supersede'd rows, so rendering is unconditional.
  const prior = row.lastFailedAttempt;

  // Defensive duration guard — ffprobe sometimes returns `NaN` for browser-
  // recorded WebM containers whose header never got finalized. Server-side
  // Phase 3.5 backfills this from the normalized WAV, but legacy rows
  // written before that fix can still surface `NaN`; treat non-finite as
  // "no duration info" rather than rendering "NaNs".
  const probeDur = row.normalize?.probeDurationSec;
  const probeDurText = Number.isFinite(probeDur)
    ? `${probeDur.toFixed(1)}s`
    : null;

  return (
    <Table.Row warning={row.cloneStatus === 'failed' || row.cloneStatus === 'elabs_failed'}>
      {/*
        Long values in the next four cells (Lit Group, Book, Variation, and
        the errorMessage block further down) used to wrap or break the table
        layout. <TruncatedTextCell> clips them to a fixed maxWidth with an
        ellipsis and reveals the full string on hover via Semantic <Popup>.
        Container <Table.Cell>s deliberately set `overflow: 'hidden'` so the
        truncation can't be defeated by inline content that would otherwise
        force the column wider.
      */}
      <Table.Cell style={{ overflow: 'hidden' }}>
        <TruncatedTextCell text={row.litGroupName} maxWidth={160} />
      </Table.Cell>
      <Table.Cell style={{ overflow: 'hidden' }}>
        <TruncatedTextCell text={row.bookTitle} maxWidth={110} />
      </Table.Cell>
      <Table.Cell>{row.characterName || '—'}</Table.Cell>
      <Table.Cell style={{ overflow: 'hidden' }}>
        <TruncatedTextCell
          text={row.variationName || row.emotion}
          maxWidth={120}
        />
      </Table.Cell>
      <Table.Cell>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
        >
          <NormalizeStatusBadge normalize={row.normalize} />
          {canRetryNormalize && (
            <Button
              size="mini"
              compact
              icon
              title={
                normalizeKey === 'none'
                  ? 'Normalize this legacy sample'
                  : 'Retry normalization'
              }
              loading={normalizeBusy}
              disabled={normalizeBusy}
              onClick={() => onAction('retryNormalize', row)}
            >
              <Icon name="redo" />
            </Button>
          )}
        </div>
        {(probeDurText || row.normalize?.probeCodec) && (
          <div style={{ color: '#888', fontSize: '0.75em', marginTop: 2 }}>
            {probeDurText || 'unknown duration'} · {row.normalize?.probeCodec || '?'}
          </div>
        )}
      </Table.Cell>
      <Table.Cell>
        <CloneStatusBadge statusKey={row.cloneStatus} />
        {row.attemptNumber > 1 && (
          <Label size="mini" style={{ marginLeft: 6 }}>
            #{row.attemptNumber}
          </Label>
        )}
        {row.errorMessage && (
          // Error blurbs from ElevenLabs / our own pipeline can be quite
          // verbose. We don't want them eating cell width or competing with
          // the Clone Status badge for visual attention. Show only a small
          // red warning triangle next to the badge as the *hint* of an error,
          // and serve the full text in a hover Popup so the BC can investigate
          // when (and only when) they're interested.
          <Popup
            content={row.errorMessage}
            mouseEnterDelay={150}
            position="top left"
            wide
            trigger={
              <Icon
                name="exclamation triangle"
                color="red"
                style={{ marginLeft: 6, cursor: 'help' }}
              />
            }
          />
        )}
        {prior && (
          <div
            style={{ color: '#b08900', fontSize: '0.75em', marginTop: 2 }}
            title={prior.errorMessage || 'Prior attempt failed'}
          >
            <Icon name="history" size="small" />
            Prior attempt #{prior.attemptNumber} failed
            {prior.errorMessage ? `: ${prior.errorMessage}` : ''} — sample has
            been re-normalized; click Seed for a fresh attempt.
          </div>
        )}
      </Table.Cell>
      <Table.Cell>
        {row.elevenLabsVoiceId ? (
          <code style={{ fontSize: '0.8em' }}>{row.elevenLabsVoiceId}</code>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )}
      </Table.Cell>
      <Table.Cell>{fmtDate(row.updatedAt || row.approvedAt)}</Table.Cell>
      <Table.Cell textAlign="right">
        {notSeeded ? (
          <Button
            size="mini"
            primary={seedAllowed}
            icon
            labelPosition="left"
            loading={actionStatus === 'seeding'}
            disabled={isBusy || !seedAllowed}
            title={seedTooltip}
            onClick={() => onAction('seed', row)}
          >
            <Icon name={seedAllowed ? 'rocket' : 'hourglass half'} />
            {seedAllowed ? 'Seed' : 'Seed (normalize first)'}
          </Button>
        ) : (
          <Button.Group size="mini">
            <Button
              icon
              title="Reconcile with ElevenLabs"
              loading={actionStatus === 'reconciling'}
              disabled={isBusy || terminal}
              onClick={() => onAction('reconcile', row)}
            >
              <Icon name="sync" />
            </Button>
            <Button
              icon
              title="Retry (new attempt)"
              loading={actionStatus === 'retrying'}
              disabled={isBusy}
              onClick={() => onAction('retry', row)}
            >
              <Icon name="redo" />
            </Button>
            <Button
              icon
              title="Smoke-test (TTS preview)"
              loading={actionStatus === 'smokeTesting'}
              // Enable whenever a voice exists (elevenLabsVoiceId set) — including a
              // clone that FAILED at the smoke-test step. Re-smoke re-uses the voice
              // (no new ElevenLabs slot); only clones that failed BEFORE getting a
              // voice (no voiceId) stay disabled.
              disabled={isBusy || !row.elevenLabsVoiceId}
              onClick={() => onOpenSmokeTest(row)}
            >
              <Icon name="play" />
            </Button>
            <Button
              icon
              title="Delete from ElevenLabs"
              loading={actionStatus === 'deleting'}
              disabled={isBusy}
              onClick={() => onAction('delete', row)}
            >
              <Icon name="trash" />
            </Button>
          </Button.Group>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

// ---------- main page --------------------------------------------------------

const VoiceCloningPipeline = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.user.profile?.data);
  const rows = useSelector(selectCloneRows);
  const status = useSelector(selectCloneStatus);
  const error = useSelector(selectCloneError);
  const filter = useSelector(selectCloneFilter);
  const counts = useSelector(selectCloneStatusCounts);
  const lastSmoke = useSelector(selectLastSmokeTest);
  const deferredSeed = useSelector(selectDeferredSeed);
  const isMock = useSelector(selectMockMode);

  const [textSearch, setTextSearch] = useState('');
  const [smokeModal, setSmokeModal] = useState({ open: false, row: null, text: '' });

  // -- initial load ----------------------------------------------------------
  useEffect(() => {
    if (currentUser?.userId) {
      // eslint-disable-next-line no-console
      console.log(
        `[VoiceCloningPipeline] mount/user-change → fetchPipeline(userId=${currentUser.userId})`
      );
      dispatch(fetchPipeline(currentUser.userId));
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[VoiceCloningPipeline] waiting for currentUser.userId (state.user.profile.data.userId)'
      );
    }
  }, [dispatch, currentUser?.userId]);

  // -- trace row-count whenever the store changes ---------------------------
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      `[VoiceCloningPipeline] rows in store=${rows.length} status=${status} mock=${isMock}`,
      rows.length > 0 ? { firstRow: rows[0] } : ''
    );
  }, [rows, status, isMock]);

  // -- Phase 4a follow-up: storyEditor fetch on filter.bookId -----------------
  // If the BC lands on this page with a bookId filter already applied (deep
  // link from the landing page, or direct URL), ensure the story-text slice
  // gets populated so the ScriptVersionSelector embedded in the full strip
  // has real processedVersionItems instead of its disabled empty state.
  // Guarded on fetchFullBookDataSetStatus === 'idle' so a previously mounted
  // worksheet or AIFileEditor's fetch isn't re-issued here.
  const storyTextFullBookStatus = useSelector(
    (s) => s.user?.storyText?.fetchFullBookDataSetStatus
  );
  useEffect(() => {
    if (!filter.bookId || !currentUser?.userId) return;
    if (storyTextFullBookStatus && storyTextFullBookStatus !== 'idle') return;
    // eslint-disable-next-line no-console
    console.log(
      `[VoiceCloningPipeline] dispatching fetchFullBookDataSet for filter.bookId=${filter.bookId} ` +
      `(storyTextFullBookStatus=${storyTextFullBookStatus || 'idle'})`
    );
    dispatch(clearStory());
    dispatch(
      fetchFullBookDataSet({
        userId: currentUser.userId,
        bookUuid: filter.bookId
      })
    );
  }, [dispatch, filter.bookId, currentUser?.userId, storyTextFullBookStatus]);


  // -- clone-complete invalidation (§9.3) -----------------------------------
  // Watch the rows array for any clone flipping into `success` /
  // `elabs_completed`; dispatch invalidateBookVoiceStats so the Coverage and
  // Clones cells immediately refetch rather than waiting for the 30s TTL.
  // Keyed by row.uuid → lastStatus, so we only fire on the *transition*.
  const prevStatusMap = useRef(new Map());
  useEffect(() => {
    if (!rows.length) return;
    const prev = prevStatusMap.current;
    const invalidated = new Set();
    rows.forEach((r) => {
      const was = prev.get(r.uuid);
      const now = r.cloneStatus;
      const becameSuccess =
        (now === 'success' || now === 'elabs_completed') &&
        was &&
        was !== now;
      if (becameSuccess && r.bookId && !invalidated.has(r.bookId)) {
        // eslint-disable-next-line no-console
        console.log(
          `[VoiceCloningPipeline] clone completed for book=${r.bookId} (row ${r.uuid}, ${was}→${now}); invalidating voice stats.`
        );
        dispatch(invalidateBookVoiceStats({ bookId: r.bookId }));
        invalidated.add(r.bookId);
      }
      prev.set(r.uuid, now);
    });
  }, [rows, dispatch]);

  // -- gentle poll of in-flight rows (refetch entire pipeline) ---------------
  // "In flight" now covers *both* pipeline halves — we poll if any row has a
  // clone-side state that still moves (queued, exporting, elabs_in_process…)
  // OR if its VoiceSnippet normalize-side state is still progressing
  // (pending, processing). Terminal rows and 'none' rows are left alone.
  const pollTimer = useRef(null);
  useEffect(() => {
    const inFlight = rows.some(
      (r) =>
        IN_FLIGHT_STATUSES.includes(r.cloneStatus) ||
        IN_FLIGHT_NORMALIZE_STATUSES.includes(r.normalize?.status)
    );
    if (!inFlight) {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    if (pollTimer.current) return; // already polling

    pollTimer.current = setInterval(() => {
      if (currentUser?.userId) dispatch(fetchPipeline(currentUser.userId));
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [rows, dispatch, currentUser?.userId]);

  // -- derived ---------------------------------------------------------------
  const filteredRows = useMemo(() => {
    let out = rows;
    if (filter.status && filter.status !== 'all') {
      out = out.filter((r) => r.cloneStatus === filter.status);
    }
    if (filter.bookId) out = out.filter((r) => r.bookId === filter.bookId);
    if (filter.litGroupId)
      out = out.filter((r) => r.litGroupId === filter.litGroupId);
    if (textSearch.trim()) {
      const q = textSearch.trim().toLowerCase();
      out = out.filter((r) =>
        [
          r.characterName,
          r.variationName,
          r.bookTitle,
          r.litGroupName,
          r.elevenLabsVoiceId
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return sortRows(out);
  }, [rows, filter, textSearch]);

  // -- build filter dropdown options ----------------------------------------
  const bookOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r.bookId && !seen.has(r.bookId)) {
        seen.set(r.bookId, r.bookTitle || r.bookId);
      }
    });
    return [
      { key: '__all__', value: null, text: 'All Books' },
      ...Array.from(seen.entries()).map(([uuid, title]) => ({
        key: uuid,
        value: uuid,
        text: title
      }))
    ];
  }, [rows]);

  const statusOptions = useMemo(
    () => [
      { key: 'all', value: 'all', text: 'All Statuses' },
      ...CLONE_STATUSES.map((s) => ({
        key: s.key,
        value: s.key,
        text: `${s.label} (${counts[s.key] || 0})`
      }))
    ],
    [counts]
  );

  // -- action handlers -------------------------------------------------------
  // Wrap handlers in `useCallback` so `React.memo(CloneRowContainer)` below can
  // skip a re-render when only unrelated fields (e.g. the polling timestamp)
  // change. Without stable references these props flip every render and memo
  // degrades to a no-op.
  const handleAction = useCallback((kind, row) => {
    switch (kind) {
      case 'seed':
        dispatch(seedClone(row.sampleId));
        break;
      case 'reconcile':
        if (row.cloneUuid) dispatch(reconcileClone(row.cloneUuid));
        break;
      case 'retry':
        if (row.cloneUuid) dispatch(retryClone(row.cloneUuid));
        break;
      case 'retryNormalize':
        // Re-enqueue the SampleNormalizer for this VoiceSnippet. Works for
        // legacy null-status rows AND rows stuck at normalizeStatus='failed'.
        if (row.normalize?.voiceSnippetUuid) {
          dispatch(retryNormalize(row.normalize.voiceSnippetUuid));
        }
        break;
      case 'delete':
        if (!row.cloneUuid) break;
        if (
          window.confirm(
            `Delete voice clone for "${row.characterName}" — "${row.variationName}" from ElevenLabs? The sample will remain approved; you can Seed a new clone attempt afterwards.`
          )
        ) {
          dispatch(deleteClone(row.cloneUuid));
        }
        break;
      default:
        break;
    }
  }, [dispatch]);

  const openSmokeTest = useCallback((row) =>
    setSmokeModal({
      open: true,
      row,
      text: `Hello, I am ${row.characterName || 'your character'}.`
    }),
    []
  );

  // ---- Fix A: stabilize the modal <audio src> across polls ----------------
  // Background: the server re-signs `smokeTestUrl` on every `/pipeline` call
  // (new signed-URL signature/expiry each request). Without this cache, every
  // 5s poll while the modal is open would swap the URL the browser is
  // currently streaming from — causing `<audio>` to reload and restart
  // playback mid-sample. We cache the first non-null URL per cloneUuid and
  // only invalidate when the user kicks off a new smoke-test (which produces
  // genuinely new audio) or closes+reopens the modal for the same row.
  //
  // The server-side presigned URL lives for 1h, which vastly exceeds any
  // realistic modal session, so holding the first URL we saw is safe.
  const audioUrlCacheRef = useRef(new Map()); // cloneUuid → stable URL

  const runSmokeTest = useCallback(() => {
    if (!smokeModal.row?.cloneUuid) return;
    // New synthesis → the URL will point at fresh audio shortly, drop the
    // stale cached one so the modal picks up the new bytes.
    audioUrlCacheRef.current.delete(smokeModal.row.cloneUuid);
    dispatch(
      smokeTestClone({
        cloneUuid: smokeModal.row.cloneUuid,
        text: smokeModal.text
      })
    );
  }, [dispatch, smokeModal.row, smokeModal.text]);

  // Grab the latest smoke-test audio URL for the row currently in the modal.
  // Backend writes `smokeTestUrl` onto the VoiceClone after the TTS worker
  // finishes; the poll will surface it.
  const modalRow = smokeModal.row
    ? rows.find((r) => r.uuid === smokeModal.row.uuid) || smokeModal.row
    : null;

  const modalPreviewUrl = useMemo(() => {
    const cache = audioUrlCacheRef.current;
    const cuid = modalRow?.cloneUuid;
    if (!cuid) return null;
    // Adopt the URL the server sent the first time we see one for this clone;
    // pin to the cached value thereafter so `<audio src>` stays referentially
    // stable across polls.
    if (!cache.has(cuid) && modalRow.smokeTestUrl) {
      cache.set(cuid, modalRow.smokeTestUrl);
    }
    return cache.get(cuid) || modalRow.smokeTestUrl || null;
  }, [modalRow?.cloneUuid, modalRow?.smokeTestUrl]);


  // -- render ----------------------------------------------------------------
  return (
    <Container style={{ padding: '20px 0', marginBottom: "40px" }}>
      <Header as="h2" dividing>
        <Icon name="cogs" />
        <Header.Content>
          Voice Cloning Pipeline
          <Header.Subheader>
            Approved speech samples exported to ElevenLabs. Track progress,
            reconcile, retry, or smoke-test completed clones.
          </Header.Subheader>
        </Header.Content>
      </Header>

      {isMock && (
        <Message warning icon>
          <Icon name="flask" />
          <Message.Content>
            <Message.Header>ElevenLabs mock mode is enabled</Message.Header>
            <p>
              The server is running with <code>ELEVENLABS_MOCK=1</code>. No real
              API calls will be made; exports and smoke-tests return a dummy
              voice_id and a placeholder audio file so you can exercise the
              pipeline end-to-end without burning credits.
            </p>
          </Message.Content>
        </Message>
      )}

      {/*
        Deferred-seed banner. The server's Seed endpoint returns
        `{ deferred: true, reason, normalizeStatus }` whenever it refuses
        because the source isn't normalized yet. Without this banner the user
        sees… nothing — the row just re-renders unchanged, which makes it
        look like the click silently failed. Showing the reason (and pointing
        at the Normalize column as the remediation) keeps the UI honest.
      */}
      {deferredSeed && (
        <Message
          warning
          icon
          onDismiss={() => dispatch(clearDeferredSeed())}
        >
          <Icon name="hourglass half" />
          <Message.Content>
            <Message.Header>Seed deferred — normalize first</Message.Header>
            <p>
              Seeding was skipped because the source audio isn't ready yet
              (<code>normalizeStatus = {deferredSeed.normalizeStatus || 'unknown'}</code>).{' '}
              Click the <Icon name="redo" size="small" /> <strong>Retry</strong> button
              in the <em>Normalize</em> column for that row; once the badge
              turns green (<em>Normalized</em>), the clone will auto-seed
              automatically.
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Stats --------------------------------------------------------- */}
      <Segment>
        <Grid columns={SUMMARY_STATUS_KEYS.length} stackable>
          {CLONE_STATUSES.filter((s) => SUMMARY_STATUS_KEYS.includes(s.key)).map(
            (s) => (
              <Grid.Column key={s.key} textAlign="center">
                <Statistic size="mini">
                  <Statistic.Value>
                    <Label color={s.color} size="large">
                      <Icon name={s.icon} />
                      {counts[s.key] || 0}
                    </Label>
                  </Statistic.Value>
                  <Statistic.Label>{s.label}</Statistic.Label>
                </Statistic>
              </Grid.Column>
            )
          )}
        </Grid>
      </Segment>

      {/* Filter bar ---------------------------------------------------- */}
      <Segment>
        <Grid stackable columns="equal" verticalAlign="middle">
          <Grid.Column>
            <Dropdown
              fluid
              selection
              options={statusOptions}
              value={filter.status || 'all'}
              onChange={(_, { value }) => dispatch(setFilter({ status: value }))}
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              fluid
              selection
              options={bookOptions}
              value={filter.bookId}
              onChange={(_, { value }) => dispatch(setFilter({ bookId: value }))}
            />
          </Grid.Column>
          <Grid.Column>
            <Input
              fluid
              icon="search"
              placeholder="Search character, voice id…"
              value={textSearch}
              onChange={(_, { value }) => setTextSearch(value)}
            />
          </Grid.Column>
          <Grid.Column width={5} textAlign="right">
            <CloneStatusLegend compact />
            <Button
              icon
              labelPosition="left"
              onClick={() => {
                dispatch(clearFilter());
                setTextSearch('');
              }}
              style={{ marginLeft: 8 }}
            >
              <Icon name="eraser" />
              Clear
            </Button>
            <Button
              icon
              labelPosition="left"
              primary
              onClick={() =>
                currentUser?.userId && dispatch(fetchPipeline(currentUser.userId))
              }
              style={{ marginLeft: 8 }}
            >
              <Icon name="refresh" />
              Refresh
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Error / Loader ------------------------------------------------ */}
      {status === 'loading' && rows.length === 0 && (
        <Segment basic textAlign="center">
          <Loader active inline="centered" content="Loading voice-clone pipeline…" />
        </Segment>
      )}
      {error && (
        <Message negative>
          <Message.Header>Failed to load pipeline</Message.Header>
          <p>{error.message || String(error)}</p>
        </Message>
      )}

      {/*
        §8.2 — Full voice-readiness strip. We only render it when the Book
        filter narrows the view to a single book; an aggregate across "all
        books" would be misleading (gaps depend on a specific book's script).
        The strip owns its own fetch/TTL, so appearing/disappearing here is
        cheap.
      */}
      {filter.bookId && (
        <BookVoiceStatsStrip
          bookId={filter.bookId}
          variant="full"
          className="vcp-voice-stats-strip"
        />
      )}

      {/*
        Table ---------------------------------------------------------
        Fix E (2026-04-24): keep the <Segment>+<Table> mounted across polls.
        Previously this was gated on `status !== 'loading'`, but the slice
        flips `status` to 'loading' at the start of *every* poll (via
        `fetchPipeline.pending`). That caused the whole segment to unmount
        on each 5s tick and remount on fulfilled — producing a visible
        blank-then-redraw flicker that also killed the React.memo savings
        from Fix D (rows can't bail if their parent is unmounted).
        The only time we truly want to hide the table is on the *initial*
        load, before we have any rows to show; subsequent polls must keep
        the existing rows visible.
      */}
      {!(status === 'loading' && rows.length === 0) && (
        <Segment>

          {filteredRows.length === 0 ? (
            <Message info>
              <Message.Header>No approved samples to show</Message.Header>
              <p>
                When a voice actor submits a sample and you approve it, it will
                appear here. Use the <strong>Seed</strong> button on each row
                to launch the ElevenLabs cloning pipeline.
              </p>
            </Message>
          ) : (
            <Table celled striped compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Literary Group</Table.HeaderCell>
                  <Table.HeaderCell>Book</Table.HeaderCell>
                  <Table.HeaderCell>Character</Table.HeaderCell>
                  <Table.HeaderCell>Variation</Table.HeaderCell>
                  <Table.HeaderCell>Normalize</Table.HeaderCell>
                  <Table.HeaderCell>Clone Status</Table.HeaderCell>
                  <Table.HeaderCell>ElevenLabs Voice ID</Table.HeaderCell>
                  <Table.HeaderCell>Updated</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredRows.map((row) => (
                  <CloneRowContainer
                    key={row.uuid}
                    row={row}
                    onAction={handleAction}
                    onOpenSmokeTest={openSmokeTest}
                  />
                ))}
              </Table.Body>
            </Table>
          )}
        </Segment>
      )}

      {/* Smoke-test modal --------------------------------------------- */}
      <Modal
        open={smokeModal.open}
        onClose={() => {
          setSmokeModal({ open: false, row: null, text: '' });
          dispatch(clearLastSmokeTest());
        }}
        size="small"
      >
        <Modal.Header>
          Smoke-Test Voice Clone
          {smokeModal.row && ` — ${smokeModal.row.characterName}`}
        </Modal.Header>
        <Modal.Content>
          <p>
            Type a line of text and click <strong>Synthesize</strong>. The
            ElevenLabs clone will speak it back so you can confirm the voice is
            what you expect.
          </p>
          <Form>
            <Form.Field>
              <label>Text to synthesize</label>
              <TextArea
                rows={3}
                value={smokeModal.text}
                onChange={(_, { value }) =>
                  setSmokeModal((m) => ({ ...m, text: value }))
                }
              />
            </Form.Field>
          </Form>
          {modalPreviewUrl && (
            <Segment basic>
              <p style={{ fontSize: '0.85em', color: '#666' }}>
                Preview ready. If it doesn't start automatically, press ▶.
              </p>
              <audio
                src={modalPreviewUrl}
                controls
                autoPlay
                style={{ width: '100%' }}
              />
            </Segment>
          )}
          {!modalPreviewUrl &&
            lastSmoke?.cloneUuid === smokeModal.row?.cloneUuid && (
              <Message info size="mini">
                <Icon name="circle notch" loading /> Synthesizing… the preview
                URL will appear here on the next refresh.
              </Message>
            )}

          {/* EVERY VERSION OF THIS VARIATION'S VOICE, back to back.
            *
            * A sample can carry a per-emotion clone and a pooled one — one voice built from this
            * recording alone, one built from several of the character's recordings and serving
            * every emotion. Deciding whether pooling is good enough means hearing them against
            * each other, and until now only the newest was reachable.
            *
            * The smoke test is the same sentence for every clone, which is what makes this a fair
            * comparison: the text is constant, so the only difference is the voice.
            *
            * URLs come from the row captured when the modal opened, so polling cannot swap the
            * src underneath a playing element and restart it. */}
          {(smokeModal.row?.clones?.length || 0) > 1 && (
            <Segment>
              <div style={{ fontWeight: 600, marginBottom: '0.25em' }}>
                Compare versions of this voice
              </div>
              <p style={{ fontSize: '0.8em', color: '#888', marginTop: 0 }}>
                Same sentence, different clones. “Serving” is the one renders will use.
              </p>
              {smokeModal.row.clones.map((c) => (
                <div key={c.uuid} style={{ marginBottom: '0.9em' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 2 }}>
                    <Label size="mini" color={c.cloneKind === 'ivc-pooled' ? 'violet' : 'blue'}>
                      {c.cloneKind === 'ivc-pooled' ? 'Pooled' : 'Per-emotion'}
                    </Label>
                    {c.isActive
                      ? <Label size="mini" color="green">serving</Label>
                      : <Label size="mini" basic>retired</Label>}
                    {c.cloneKind === 'ivc-pooled' && c.sourceSampleCount && (
                      <span style={{ fontSize: '0.78em', color: '#888' }}>
                        built from {c.sourceSampleCount} recording
                        {c.sourceSampleCount === 1 ? '' : 's'}
                        {c.sourceSeconds ? ` · ${Number(c.sourceSeconds).toFixed(0)}s` : ''}
                      </span>
                    )}
                    {c.cloneStatus !== 'success' && (
                      <span style={{ fontSize: '0.78em', color: '#a55' }}>{c.cloneStatus}</span>
                    )}
                  </div>
                  {c.smokeTestUrl ? (
                    <audio src={c.smokeTestUrl} controls style={{ width: '100%' }} />
                  ) : (
                    <span style={{ fontSize: '0.8em', color: '#aaa' }}>
                      No preview yet — this clone has not finished its smoke test.
                    </span>
                  )}
                </div>
              ))}
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setSmokeModal({ open: false, row: null, text: '' });
              dispatch(clearLastSmokeTest());
            }}
          >
            Close
          </Button>
          <Button primary onClick={runSmokeTest}>
            <Icon name="play" /> Synthesize
          </Button>
        </Modal.Actions>
      </Modal>

      {/*
        Standard authenticated-page footer (build/version + copyright). All
        peer landing pages render this; without it the Voice Cloning Pipeline
        page felt visually truncated. Invoked as a function call rather than
        a JSX element to match the pattern used elsewhere in the codebase.
      */}
      {AuthStdFooter()}
    </Container>
  );
};

// Separate container to pick up per-row actionStatus without re-rendering
// the whole table. The clone-side action spinner is keyed by the row uuid
// (= cloneUuid when a clone exists, else sampleId); the normalize-side retry
// is keyed by the VoiceSnippet uuid so the two spinners never fight for the
// same store slot.
//
// Wrapped in `React.memo` (Fix D) so that when a poll tick lands a row whose
// material fields haven't changed, the slice returns the same object ref
// (see `voiceCloningSlice.js` → fetchPipeline.fulfilled) and this component
// bails out on props-equality. Combined with `useCallback`-stabilized
// `onAction` / `onOpenSmokeTest` up in the parent, only rows that actually
// transitioned will re-render — previously every in-flight row redrew every
// 5s, which made tooltips flicker and kept text selections from sticking.
const CloneRowContainer = memo(({ row, onAction, onOpenSmokeTest }) => {
  // Actions are dispatched with different ids depending on which one they are — seed goes by
  // sampleId, reconcile and retry by cloneUuid — and the status map is keyed on whatever was
  // dispatched. Checking both is what keeps the spinner attached now that the row id is the
  // SAMPLE rather than the clone: keying on one would leave half the actions with no feedback.
  const sampleActionStatus = useSelector(selectActionStatus(row.sampleId || row.uuid));
  const cloneActionStatus = useSelector(selectActionStatus(row.cloneUuid || '__none__'));
  const actionStatus = sampleActionStatus || cloneActionStatus;
  const normalizeActionStatus = useSelector(
    selectActionStatus(row.normalize?.voiceSnippetUuid || '__none__')
  );
  return (
    <CloneRow
      row={row}
      onAction={onAction}
      actionStatus={actionStatus}
      normalizeActionStatus={normalizeActionStatus}
      onOpenSmokeTest={onOpenSmokeTest}
    />
  );
});
CloneRowContainer.displayName = 'CloneRowContainer';


export default VoiceCloningPipeline;
